<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper v-if="!id" :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit()" v-show="ready">
          <h4 v-if="!id">Step 4: Review &amp; Approve Risk Analysis Report</h4>
          <h3 v-else>Risk Analysis Report</h3>
          <hr />
          <p>
            A risk analysis, in accordance with the implementation specification at § 164.308(a)(1)(ii)(A), was conducted by
            <span class="ra-highlight">{{rm.seed.practice.po.name}}</span> (&quot;Privacy Officer&quot;) on
            <span class="ra-highlight">{{rm.approvedDate | formatDate('MMMM DD, YYYY')}}</span> for
            <span class="ra-highlight">{{rm.seed.practice.name}}</span> located at
            <span class="ra-highlight">
              {{rm.seed.practice.address1}}, {{rm.seed.practice.address2}}
              <span v-if="rm.seed.practice.address2">,</span>
              {{rm.seed.practice.city}}, {{rm.seed.practice.state}} {{rm.seed.practice.zip}}
            </span> (&quot;Practice&quot;).
          </p>
          <p>This report documents the risk analysis process, output of each step, and initial identification of HIPAA required and addressable safeguards. Much of the information contained within will serve as direct input to the risk management process.</p>

          <h4 class="pt-2">1. Review Workforce Members and Business Associates</h4>
          <p>Workforce members and business associates can act as a threat or vulnerability; therefore, the Privacy Officer must verify the accuracy of each the Practice’s workforce member and business associate listings.</p>

          <h6>Active Workforce Members</h6>
          <b-table class="p-1" responsive bordered hover show-empty empty-text="No active workforce members" :items="activeWorkforce" :fields="columnsActiveWorkforce">
            <template slot="cell(role)" slot-scope="data">{{roleMap.Roles[data.value]}}</template>
            <template slot="cell(access)" slot-scope="data">
              <hm-roleslist :role="rm.seed.role" :item="data.item.role" />
            </template>
          </b-table>

          <h6>Removed Workforce Members</h6>
          <b-table class="p-1" responsive bordered hover show-empty empty-text="No removed workforce members" :items="inactiveWorkforce" :fields="columnsInactiveWorkforce">
            <template slot="cell(role)" slot-scope="data">{{roleMap.Roles[data.value]}}</template>
            <template slot="cell(separation)" slot-scope="data">
              <span v-if="data.value.amicable" class="text-success">Amicable</span>
              <span v-else class="text-danger">Hostile</span>
            </template>
          </b-table>

          <h6>Business Associates</h6>
          <b-table class="p-1" responsive bordered hover show-empty empty-text="No business associates" :items="businessAssociates" :fields="columnsBA">
            <template slot="cell(signed)" slot-scope="data">
              <span v-if="data.value">
                <i class="icon-check text-success"></i> Yes
              </span>
              <span v-else>
                <i class="icon-exclamation text-danger"></i> No
              </span>
            </template>
          </b-table>

          <h4 class="pt-2">2. Identify Scope and Gather Data</h4>
          <h6>Scope</h6>
          <p>
            All activities that create, store, and transmit ePHI and the information systems that support these business processes where considered when gathering electronic media data. Electronic media is defined, in §160.103, as: (1) Electronic storage media including
            memory devices in computers (hard drives) and any removable/transportable digital memory medium, such as magnetic tape or disk, optical disk, or digital memory card; or (2) Transmission media used to exchange information already in
            electronic storage media. Transmission media include, for example, the internet (wide-open), extranet (using internet technology to link a business with information accessible only to collaborating parties), leased lines, dial-up lines,
            private networks, and the physical movement of removable/transportable electronic storage media.
          </p>
          <p>Certain transmissions, including of paper, via facsimile, and of voice, via telephone, are not considered to be transmissions via electronic media, because the information being exchanged did not exist in electronic form before the transmission.</p>

          <h6>Data</h6>
          <p>The Practice has identified where ePHI is stored, received, maintained or transmitted by reviewing past and/or existing projects, performing internal interviews, and/or reviewing documentation.</p>
          <b-table class="p-1" responsive bordered hover show-empty empty-text="No inventory items" :items="inventory" :fields="columnsInventory">
            <template slot="cell(type)" slot-scope="data">{{inventoryMap.Type[data.value]}}</template>
          </b-table>

          <h4 class="pt-2">3. Identify Potential Threats and Vulnerabilities</h4>
          <p>
            The Practice has identified reasonably anticipated threats and vulnerabilities to EPHI unique to the circumstances of the Practice. The identification of threats and vulnerabilities are so closely related in the risk analysis process that they are identified
            at the same time.
          </p>
          <h6>Threat</h6>
          <p>&quot;The potential for a person or thing to exercise (accidentally trigger or intentionally exploit) a specific vulnerability.&quot;</p>

          <h6>Vulnerability</h6>
          <p>&quot;A flaw or weakness in system security procedures, design, implementation, or internal controls that could be exercised (accidentally triggered or intentionally exploited) and result in a security breach or a violation of the system’s security policy.&quot;</p>

          <h6>Reasonably Anticipated Threat - Vulnerability Combinations</h6>
          <div class="pt-2" v-for="threatGroup in threatsByType" :key="'3-' + threatGroup.type">
            <u class="text-capitalize">{{threatGroup.type}}</u>
            <div v-for="threat in threatGroup.threats" :key="'3-' + threat.id">
              <i class="fa fa-caret-right pr-2"></i>
              {{threat.threat}} - {{threat.vuln}}
            </div>
          </div>

          <h4 class="pt-4">4. Assess Current Security Measures</h4>
          <p>Analyze both technical and non-technical security measures (safeguards) currently implemented to minimize or eliminate risks to ePHI.</p>

          <h6>Currently Implemented Safeguards</h6>
          <div v-for="(item, index) in safeguardsPos" :key="'4-' + index">
            <div class="pl-3">
              <i class="icon-check text-success"></i>
              {{item.title}}
              <div v-if="item.list">
                <div class="ml-3" v-for="(line, index) in item.list" :key="'4--' + index">
                  <i class="fa fa-caret-right pr-2"></i>
                  {{line}}
                </div>
              </div>
            </div>
          </div>

          <h4 class="pt-4">5. Determine Likelihood of Threat Occurrence</h4>
          <p>
            &quot;Likelihood of occurrence&quot; is the probability that a threat will trigger or exploit a specific vulnerability. The Practice considered each potential threat and vulnerability combination and rated them by likelihood (or probability) that the
            combination would occur. The following likelihood rating scale was used:
          </p>
          <p class="pl-3">
            (1) Rare: insignificant chance of occurrence (once every 15+ years)
            <br />(2) Not Likely: minimal chance of occurrence (once every 10 years)
            <br />(3) Likely: modest chance of occurrence (once every 3 years)
            <br />(4) Very Likely: probable chance of occurrence (once every year)
            <br />(5) Certain: almost certain chance of occurrence (once every month)
          </p>

          <h6>Likelihood Rating Associated With Each Threat-Vulnerability Combination</h6>
          <div class="pt-2" v-for="threatGroup in threatsByType" :key="'5-' + threatGroup.type">
            <u class="text-capitalize">{{threatGroup.type}}</u>
            <div v-for="threat in threatGroup.threats" :key="'5-' + threat.id">
              <i class="fa fa-caret-right pr-2"></i>
              {{threat.threat}} - {{threat.vuln}}:
              <b-badge class="px-2">{{threat.chance}}</b-badge>
            </div>
          </div>

          <h4 class="pt-4">6. Determine Potential Impact of Threat Occurrence</h4>
          <p>The Practice rated potential impacts associated with the occurrence of threats triggering or exploiting vulnerabilities that affect the confidentiality, availability and integrity of ePHI. The following impact rating scale was used:</p>
          <p class="pl-3">
            (1) Insignificant: a single or just a handful of medical records lost or compromised
            <br />(2) Low: moderate number of medical records lost or compromised
            <br />(3) Medium: significant number of medical records lost or compromised
            <br />(4) High: large number of medical records (500+) lost or compromised
            <br />(5) Devastating: very large number of medical records (500+) lost or compromised
          </p>

          <h6>Impact Rating Associated With Each Threat-Vulnerability Combination</h6>
          <div class="pt-2" v-for="threatGroup in threatsByType" :key="'6-' + threatGroup.type">
            <u class="text-capitalize">{{threatGroup.type}}</u>
            <div v-for="threat in threatGroup.threats" :key="'6-' + threat.id">
              <i class="fa fa-caret-right pr-2"></i>
              {{threat.threat}} - {{threat.vuln}}:
              <b-badge class="px-2">{{threat.impact}}</b-badge>
            </div>
          </div>

          <h4 class="pt-4">7. Determine Level of Risk</h4>
          <p>Risk is a function determined by the likelihood of a given threat triggering or exploiting a specific vulnerability and the resulting impact. The Practice used the output of the previous two steps (steps 4 and 5) as inputs to this step.</p>
          <p>
            A risk level matrix is used to assist in determining risk levels. A risk level matrix is created using the values for likelihood of threat occurrence and resulting impact of threat occurrence. The matrix is populated using a 1 - 5 point rating system.
            For example, a threat likelihood value of &quot;5&quot; combined with an impact value of &quot;2&quot; equals a risk level of &quot;10.&quot; Or a threat likelihood value of &quot;3&quot; combined with an impact value of &quot;1&quot;
            may equal a risk level of &quot;3.&quot;
          </p>
          <p>
            Risk levels are labeled with a general action description to help guide senior management decision making. The action description identifies the general timeline and type of response needed to reasonably and appropriately reduce the risk to acceptable
            levels. The following are action descriptions assigned to risk level ranges:
          </p>
          <p class="pl-3">
            Risk level 20-25:
            <span class="text-danger">Immediate implementation of corrective measures</span>
            <br />Risk level 12-19:
            <span class="text-warning">Near future implementation of corrective measures</span>
            <br />Risk level 06-11:
            <span class="text-primary">Earliest convenience implementation of corrective measures</span>
            <br />Risk level 01-05:
            <span class="text-success">Least urgent implementation of corrective measures</span>
          </p>

          <h6>Level of Risk Associated With Each Threat-Vulnerability Combination</h6>
          <div class="pt-2" v-for="threatGroup in threatsByType" :key="'7-' + threatGroup.type">
            <u class="text-capitalize">{{threatGroup.type}}</u>
            <div v-for="threat in threatGroup.threats" :key="'7-' + threat.id">
              <i class="fa fa-caret-right pr-2"></i>
              {{threat.threat}} - {{threat.vuln}}:
              <span class="text-danger" v-if="threat.risk >= 20">
                <b-badge variant="danger" class="mx-2">{{threat.risk}}</b-badge>(Immediate implementation of corrective measures)
              </span>
              <span class="text-warning" v-if="threat.risk >= 12 && threat.risk <= 19">
                <b-badge variant="warning" class="mx-2">{{threat.risk}}</b-badge>(Near future implementation of corrective measures)
              </span>
              <span class="text-primary" v-if="threat.risk >= 6 && threat.risk <= 11">
                <b-badge variant="primary" class="mx-2">{{threat.risk}}</b-badge>(Earliest convenience implementation of corrective measures)
              </span>
              <span class="text-success" v-if="threat.risk <= 5">
                <b-badge variant="success" class="mx-2">{{threat.risk}}</b-badge>(Least urgent implementation of corrective measures)
              </span>
            </div>
          </div>

          <h4 class="pt-4">8. Identify Security Measures</h4>
          <p>
            Security measures were identified that can be used to reduce risk to a reasonable and appropriate level. The Practice considered factors such as: the effectiveness of the security measure; legislative or regulatory requirements that require certain security
            measures to be implemented; and requirements of the organization’s policies and procedures. Additional security measures may be identified during the risk management process.
          </p>
          <p>The evaluation, prioritization, modification, and implementation of security measures is part of the risk management process.</p>

          <h6>Security Measures Not Currently Implemented</h6>

          <div v-if="countKeys(safeguardsNeg) == 0">None to report.</div>
          <div v-for="(item, index) in safeguardsNeg" :key="'8-' + index">
            <div class="pl-3">
              <i class="icon-exclamation text-danger"></i>
              {{item.title}}
              <div v-if="item.list">
                <div class="ml-3" v-for="(line, index) in item.list" :key="'8--' + index">
                  <i class="fa fa-caret-right pr-2"></i>
                  {{line}}
                </div>
              </div>
            </div>
          </div>

          <h4 class="pt-4">9. Approval</h4>
          <p>This Risk Analysis Report certifies and documents a risk analysis was completed, reviewed and approved by the Practice’s Privacy Officer.</p>
          <p>
            Privacy Officer:
            <span class="ra-highlight">{{rm.seed.practice.po.name}}</span>
            <br />Approved on:
            <span class="ra-highlight">{{rm.approvedDate|formatDate('YYYY-MM-DD HH:MM:SS')}}</span>
          </p>

          <hr />
          <b-row v-if="!id">
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/level">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Approve</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>

    <!-- Approved Modal Dialog -->
    <b-modal id="dlgApproved" class="modal-primary" hide-header-close v-model="dlgApproved">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fa-rocket pr-2"></i>Risk Analysis Complete
      </div>
      <p>Congratulations! You've completed a risk analysis. Next you'll create a risk management plan resulting in the creation of mitigation tasks (if any).</p>
      <div slot="modal-footer">
        <b-button variant="primary" class="ml-2" @click="onClose">Continue</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskAnalysisReport',
  props: ['id'],
  data() {
    return {
      rm: {
        threats: [],
        seed: {
          practice: {
            po: {}
          },
          workforce: []
        }
      },
      safeguards: [],
      columnsActiveWorkforce: [{
        key: 'name',
        label: 'Name'
      }, {
        key: 'role',
        label: 'Role'
      }, {
        key: 'access',
        label: 'ePHI Access'
      }],
      columnsInactiveWorkforce: [{
        key: 'name',
        label: 'Name'
      }, {
        key: 'role',
        label: 'Role'
      }, {
        key: 'separation',
        label: 'Seperation'
      }],
      columnsBA: [{
        key: 'name',
        label: 'Name'
      }, {
        key: 'signed',
        label: 'Signed BA Agreement'
      }],
      columnsInventory: [{
        key: 'type',
        label: 'Type'
      }, {
        key: 'name',
        label: 'Name'
      }, {
        key: 'location',
        label: 'Location'
      }],
      dlgApproved: false,
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 4
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['inventoryMap', 'roleMap', 'safeguardsMap', 'threatMap']),
    activeWorkforce() {
      return Enumerable.from(this.rm.seed.workforce).select(x => { return x.value }).where(x => { return x.active }).toArray();
    },
    inactiveWorkforce() {
      return Enumerable.from(this.rm.seed.workforce).select(x => { return x.value }).where(x => { return !x.active }).toArray();
    },
    businessAssociates() {
      return Enumerable.from(this.rm.seed.ba).select(x => { return x.value }).toArray();
    },
    inventory() {
      return Enumerable.from(this.rm.seed.inventory).select(x => { return x.value }).toArray();
    },
    threatsByType() {
      return this.rm && this.rm.threats ? Enumerable.from(this.threatMap)
        .join(this.rm.threats,
          function (o) { return o.id + ':' + o.revision; },
          function (i) { return i.id + ':' + i.revision; },
          function (o, i) { return { id: o.id, revision: o.revision, type: o.type, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, chance: i.chance, impact: i.impact, risk: i.chance * i.impact }; })
        .where(x => { return x.active; })
        .groupBy(x => { return x.type; }, null, function (key, g) { return { type: key, threats: Enumerable.from(g).toArray() } })
        .toArray() : [];
    },
    safeguardsPos() {
      if (this.rm.seed) {
        let seed = this.rm.seed; // needed for eval
        // build list of pos test safeguards
        return Enumerable.from(this.safeguards)
          .where(x => {
            let hasLoop = x.hasOwnProperty('loop');
            return (x.posList && x.posList.length > 0) || // loop with a pos list
              (!hasLoop && x.pos && eval(x.pos)); // no loop with a pos evaluation
          })
          .select(x => { return { title: x.title, desc: x.desc, list: x.posList }; })
          .toArray();
      } else {
        return [];
      }
    },
    safeguardsNeg() {
      if (this.rm.seed) {
        let seed = this.rm.seed; // needed for eval
        // build list of neg test safeguards
        return Enumerable.from(this.safeguards)
          .where(x => {
            let hasLoop = x.hasOwnProperty('loop');
            return (x.type == 'required' || x.type == 'addressable') &&
              ((x.negList && x.negList.length > 0) || // loop with a neg list
                (!hasLoop && x.neg && eval(x.neg))); // no loop with a neg evaluation
          })
          .select(x => { return { title: x.title, desc: x.desc, list: x.negList }; })
          .toArray();
      } else {
        return [];
      }
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception'
    }),
    countKeys(obj) {
      let count = 0;
      for (var prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          ++count;
        }
      }
      return count;
    },
    updateSafeguards(safeguards) {
      this.safeguards = safeguards;

      // expand safeguards that have items to loop over
      for (let i = 0; i < this.safeguards.length; i++) {
        if (this.safeguards[i].hasOwnProperty('loop')) {
          let seed = this.rm.seed; // needed for eval
          console.log('RAEval[Loop]:', safeguards[i].loop);
          let loopEval = eval(safeguards[i].loop);

          for (let obj in loopEval) {
            let item = loopEval[obj]; // needed for eval
            let InventoryMap = this.inventoryMap; // needed for eval

            // test loop item for positive safeguard
            console.log('RAEval[Pos]:', safeguards[i].pos);
            if (eval(this.safeguards[i].pos)) {
              if (!this.safeguards[i].posList) {
                this.safeguards[i].posList = [];
              }

              console.log('RAEval[Pos List]:', safeguards[i].list);
              this.safeguards[i].posList = this.safeguards[i].posList.concat(eval(this.safeguards[i].list));
            }

            // test loop item for negative safeguard
            console.log('RAEval[Neg]:', safeguards[i].neg);
            if (eval(this.safeguards[i].neg)) {
              if (!this.safeguards[i].negList) {
                this.safeguards[i].negList = [];
              }

              console.log('RAEval[Neg List]:', safeguards[i].list);
              this.safeguards[i].negList = this.safeguards[i].negList.concat(eval(this.safeguards[i].list));
            }
          }
        }
      }
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.rm.approved = true;
      this.rm.approvedDate = new Date();
      this.rm.step = this.currentStep;

      riskService.setRiskManagement(this.rm)
        .then(data => {
          // show approved dialog
          this.dlgApproved = true;
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onClose() {
      this.dlgApproved = false;
      this.$router.push('/risk/plan');
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchInventoryMap'),
        this.$store.dispatch('maps/fetchRoleMap'),
        this.$store.dispatch('maps/fetchSafeguardsMap'),
        this.$store.dispatch('maps/fetchThreatMap'),
        this.id && this.id.length > 0 ? riskService.getRiskManagement(this.id) : riskService.getCurrentRiskManagement()
      ]).then((values) => {
        let rm = values[4];

        if (!rm.approvedDate) { // backward compatibility check
          rm.approvedDate = rm.updatedAt;
        }

        this.rm = rm;

        this.updateSafeguards(
          Enumerable.from(this.threatMap)
            .join(this.rm.threats,
              function (o) { return o.id + ':' + o.revision; },
              function (i) { return i.id + ':' + i.revision; },
              function (o, i) { return { id: o.id, safeguards: o.safeguards, active: i.active }; })
            .where(function (x) { return x.active == true }) // only active threats
            .selectMany(function (x) { return x.safeguards; }) // flatten out into safeguard array
            .distinct(function (x) { return x; }) // only return one of each safeguard
            .join(JSON.parse(JSON.stringify(this.safeguardsMap)),
              function (o) { return o; },
              function (i) { return i.id; },
              function (o, i) { return i; })
            .toArray()
        );
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>

<style lang="scss">
// import variables for this application
@import '@/assets/scss/_variables.scss';

.ra-highlight {
  color: $highlight-color;
  background-color: $highlight-bg;
  display: inline;
}
.ra-highlight-pre {
  color: $highlight-color;
  background-color: $highlight-bg;
  white-space: pre-wrap;
}
</style>